class SealHomeTestimonials extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.marqueeRows = [];
    this.hydrated = false;
    this.isVisible = false;
    this.lastFrameTime = null;
    this.rafId = null;
    this.resizeFrame = null;
    this.measuredCardHeight = 0;

    const { signal } = this.abortController;
    this.mediaQuery.addEventListener('change', () => this.reset(), { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopAnimation();
      } else if (this.isVisible) {
        this.startAnimation();
      }
    }, { signal });
    window.addEventListener('pointerup', () => this.resumeAllRows(), { signal });
    window.addEventListener('pointercancel', () => this.resumeAllRows(), { signal });
    window.addEventListener('blur', () => this.resumeAllRows(), { signal });

    if (!('IntersectionObserver' in window)) {
      this.isVisible = true;
      this.hydrate();
      return;
    }

    this.visibilityObserver = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      this.isVisible = Boolean(entry?.isIntersecting);

      if (this.isVisible) {
        this.hydrate();
        this.startAnimation();
      } else {
        this.stopAnimation();
      }
    }, { threshold: 0.01 });

    this.hydrationObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.hydrate();
      this.hydrationObserver?.disconnect();
    }, { rootMargin: '300px 0px' });

    this.visibilityObserver.observe(this);

    const rect = this.getBoundingClientRect();
    if (rect.top <= window.innerHeight + 300 && rect.bottom >= -300) {
      this.hydrate();
    } else {
      this.hydrationObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    this.hydrationObserver?.disconnect();
    this.stopAnimation();

    if (this.resizeFrame) {
      window.cancelAnimationFrame(this.resizeFrame);
    }
  }

  hydrate() {
    if (this.hydrated) return;

    const rows = Array.from(this.querySelectorAll('[data-seal-testimonials-row]'));
    for (const row of rows) {
      const sourceGroup = row.querySelector('[data-seal-testimonials-group]');
      if (!sourceGroup || row.querySelectorAll('[data-seal-testimonials-group]').length > 1) continue;

      const duplicateGroup = sourceGroup.cloneNode(true);
      duplicateGroup.setAttribute('aria-hidden', 'true');
      duplicateGroup.querySelectorAll('[data-shopify-editor-block]').forEach((element) => {
        element.removeAttribute('data-shopify-editor-block');
      });
      row.appendChild(duplicateGroup);
    }

    this.marqueeRows = rows.map((row) => ({
      row,
      cards: Array.from(row.querySelectorAll('[data-seal-testimonial-card]')),
      direction: row.dataset.direction === 'right' ? 'right' : 'left',
      positions: [],
      widths: [],
      trackLength: 0,
      cycleLength: 0,
      rowWidth: 0,
      offset: 0,
      activeIndexes: new Set(),
      hasPositioned: false,
      paused: false,
      dragging: false,
      pointerId: null,
      dragStartX: 0,
      dragStartOffset: 0,
    }));

    const { signal } = this.abortController;
    for (const state of this.marqueeRows) {
      state.row.addEventListener('pointerdown', (event) => this.startPointerInteraction(state, event), { signal });
      state.row.addEventListener('pointermove', (event) => this.movePointerInteraction(state, event), { signal });
      state.row.addEventListener('pointerup', (event) => this.endPointerInteraction(state, event), { signal });
      state.row.addEventListener('pointercancel', (event) => this.endPointerInteraction(state, event), { signal });
      state.row.addEventListener('lostpointercapture', (event) => this.endPointerInteraction(state, event), { signal });
      state.row.addEventListener('focusin', () => this.pauseRow(state), { signal });
      state.row.addEventListener('focusout', () => this.resumeRow(state), { signal });
    }

    this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
    this.resizeObserver.observe(this);
    for (const state of this.marqueeRows) {
      this.resizeObserver.observe(state.row);
    }

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.scheduleMeasure());
    }

    this.hydrated = true;
    this.hydrationObserver?.disconnect();
    this.dataset.hydrated = 'true';
    this.reset();
  }

  reset() {
    if (!this.hydrated) return;

    this.stopAnimation();
    this.measure();

    if (this.mediaQuery.matches) {
      for (const state of this.marqueeRows) {
        state.offset = 0;
        this.showAllCards(state);
      }
      return;
    }

    for (const state of this.marqueeRows) {
      this.positionCards(state);
    }

    if (this.isVisible) this.startAnimation();
  }

  startAnimation() {
    if (!this.hydrated || !this.isVisible || document.hidden || this.mediaQuery.matches || this.rafId) return;

    this.lastFrameTime = null;
    this.rafId = window.requestAnimationFrame((time) => this.animate(time));
  }

  stopAnimation() {
    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    this.lastFrameTime = null;
  }

  scheduleMeasure() {
    if (!this.hydrated || this.resizeFrame) return;

    this.resizeFrame = window.requestAnimationFrame(() => {
      this.resizeFrame = null;
      this.measure();
    });
  }

  measure() {
    const duration = parseFloat(getComputedStyle(this).getPropertyValue('--seal-home-testimonials-duration'));
    this.duration = Number.isFinite(duration) && duration > 0 ? duration * 1000 : 75000;
    this.measuredCardHeight = this.measureCardHeight();

    for (const state of this.marqueeRows) {
      if (state.cards.length === 0) continue;

      const rowStyle = getComputedStyle(state.row);
      const gap = parseFloat(rowStyle.getPropertyValue('--seal-home-testimonials-gap')) || 0;
      const groupCount = Math.max(state.row.querySelectorAll('[data-seal-testimonials-group]').length, 1);
      const cycleCardCount = Math.max(Math.round(state.cards.length / groupCount), 1);
      const positions = [];
      const widths = [];
      let cursor = 0;

      for (const card of state.cards) {
        const rect = card.getBoundingClientRect();
        const width = rect.width || card.offsetWidth || 1;

        positions.push(cursor);
        widths.push(width);
        cursor += width + gap;
      }

      state.positions = positions;
      state.widths = widths;
      state.trackLength = Math.max(cursor, 1);
      state.cycleLength = Math.max(positions[cycleCardCount] || state.trackLength / groupCount || state.trackLength, 1);
      state.rowWidth = state.row.getBoundingClientRect().width || window.innerWidth || 1;
      state.offset %= state.trackLength;
      state.activeIndexes = new Set();
      state.hasPositioned = false;

      state.row.style.setProperty('--seal-home-testimonials-row-height', `${Math.ceil(this.measuredCardHeight + 10)}px`);
      if (this.mediaQuery.matches) {
        this.showAllCards(state);
      } else {
        this.positionCards(state);
      }
    }
  }

  measureCardHeight() {
    const cards = Array.from(this.querySelectorAll('[data-seal-testimonial-card]'));
    if (cards.length === 0) {
      this.style.removeProperty('--seal-home-testimonials-card-height');
      return 0;
    }

    this.style.removeProperty('--seal-home-testimonials-card-height');

    const maxHeight = cards.reduce((height, card) => {
      const rect = card.getBoundingClientRect();
      return Math.max(height, rect.height || card.offsetHeight || 0);
    }, 0);

    const measuredHeight = Math.ceil(maxHeight);
    if (measuredHeight > 0) {
      this.style.setProperty('--seal-home-testimonials-card-height', `${measuredHeight}px`);
    }

    return measuredHeight;
  }

  animate(time) {
    this.rafId = null;
    if (!this.isVisible || document.hidden || this.mediaQuery.matches) return;

    if (this.lastFrameTime === null) {
      this.lastFrameTime = time;
    }

    const delta = Math.min(time - this.lastFrameTime, 64);
    this.lastFrameTime = time;

    for (const state of this.marqueeRows) {
      if (state.paused || state.trackLength <= 1) continue;

      const movement = (state.cycleLength / this.duration) * delta;
      state.offset = (state.offset + movement) % state.trackLength;
      this.positionCards(state);
    }

    this.rafId = window.requestAnimationFrame((nextTime) => this.animate(nextTime));
  }

  pauseRow(state) {
    state.paused = true;
  }

  resumeRow(state) {
    state.paused = false;
  }

  resumeAllRows() {
    for (const state of this.marqueeRows) {
      this.resumeRow(state);
    }
  }

  startPointerInteraction(state, event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    state.dragging = true;
    state.pointerId = event.pointerId;
    state.dragStartX = event.clientX;
    state.dragStartOffset = state.offset;
    state.row.classList.add('is-dragging');
    this.pauseRow(state);

    if (state.row.setPointerCapture) {
      state.row.setPointerCapture(event.pointerId);
    }
  }

  movePointerInteraction(state, event) {
    if (!state.dragging || state.pointerId !== event.pointerId || state.trackLength <= 1) return;

    const dragDistance = event.clientX - state.dragStartX;
    const dragOffset = state.direction === 'right' ? dragDistance : -dragDistance;

    state.offset = this.normalizeOffset(state.dragStartOffset + dragOffset, state.trackLength);
    this.positionCards(state);
  }

  endPointerInteraction(state, event) {
    if (state.pointerId !== null && state.pointerId !== event.pointerId) return;

    if (state.row.releasePointerCapture && state.pointerId !== null) {
      try {
        state.row.releasePointerCapture(state.pointerId);
      } catch (_error) {
        state.pointerId = null;
      }
    }

    state.dragging = false;
    state.pointerId = null;
    state.row.classList.remove('is-dragging');
    this.resumeRow(state);
  }

  normalizeOffset(offset, trackLength) {
    return ((offset % trackLength) + trackLength) % trackLength;
  }

  positionCards(state) {
    const buffer = 120;
    const rowWidth = state.rowWidth || window.innerWidth || 1;
    const trackLength = state.trackLength || 1;
    const nextActiveIndexes = new Set();

    state.cards.forEach((card, index) => {
      const width = state.widths[index] || card.offsetWidth || 1;
      let x = state.positions[index] || 0;

      if (state.direction === 'right') {
        x += state.offset;
        while (x > rowWidth + buffer) {
          x -= trackLength;
        }
      } else {
        x -= state.offset;
        while (x < -width - buffer) {
          x += trackLength;
        }
      }

      const isActive = x < rowWidth + buffer && x + width > -buffer;

      if (isActive || state.activeIndexes.has(index) || !state.hasPositioned) {
        card.style.setProperty('--seal-home-testimonials-card-x', `${x}px`);
        card.classList.toggle('is-active', isActive);
        card.classList.toggle('is-offscreen', !isActive);
      }

      if (isActive) nextActiveIndexes.add(index);
    });

    state.activeIndexes = nextActiveIndexes;
    state.hasPositioned = true;
  }

  showAllCards(state) {
    state.activeIndexes = new Set();
    state.hasPositioned = false;

    for (const card of state.cards) {
      card.classList.remove('is-active', 'is-offscreen');
      card.style.removeProperty('--seal-home-testimonials-card-x');
    }
  }
}

if (!customElements.get('seal-home-testimonials')) {
  customElements.define('seal-home-testimonials', SealHomeTestimonials);
}
